//#region imports
import React from 'react';
//#endregion

//#region styles
const headerStyle = {
    border: '1px solid #e5e7eb',
    padding: '10px 14px',
    textAlign: 'center',
    backgroundColor: '#f9fafb',
    fontWeight: 600,
    fontSize: '13px',
    color: '#374151',
};

const cellStyle = {
    border: '1px solid #e5e7eb',
    padding: '10px 14px',
    textAlign: 'center',
    fontSize: '13px',
    color: '#1f2937',
};

const labelCellStyle = {
    ...cellStyle,
    textAlign: 'left',
    fontWeight: 500,
};

const tableStyle = {
    border: '1px solid #e5e7eb',
    borderCollapse: 'collapse',
    width: '100%',
    tableLayout: 'fixed',
};

// Shared column widths so every summary table in the SLA Summary Report lines
// up vertically regardless of its content.
const SUMMARY_COLGROUP = (
    <colgroup>
        <col style={{ width: '40%' }} />
        <col style={{ width: '10%' }} />
        <col style={{ width: '10%' }} />
        <col style={{ width: '14%' }} />
        <col style={{ width: '12%' }} />
        <col style={{ width: '14%' }} />
    </colgroup>
);
//#endregion

//#region helpers
const parseTarget = (target) => {
    if (!target) return 0;
    return parseFloat(target.replace('%', ''));
};

const isNoDataRow = (row) => row.passReason === 'No data' || row.passReason === 'Invalid times';

const recomputeKpiFromRawData = (kpi) => {
    const rawData = kpi?.rawData;
    // Fall back to the API's pre-computed values when rawData isn't available
    // (REPORTS_ONLY role, hideBreakdown=true, or summary-only views like SLA Summary).
    if (!Array.isArray(rawData) || rawData.length === 0) {
        const totalPass = kpi?.passCount ?? kpi?.totalPass ?? 0;
        const total = kpi?.totalAssisted ?? kpi?.total ?? 0;
        const score = total > 0 ? Math.round((totalPass / total) * 100) : 0;
        return { ...kpi, totalPass, total, score };
    }
    const filtered = rawData.filter((row) => !isNoDataRow(row));
    const totalPass = filtered.filter((row) => row.pass === true).length;
    const total = filtered.length;
    const score = total > 0 ? Math.round((totalPass / total) * 100) : 0;

    return { ...kpi, totalPass, total, score };
};

const getKpisForReport = (report, includeNoData) => {
    const kpis = report?.kpis || {};
    if (includeNoData) return kpis;
    const result = {};
    Object.keys(kpis).forEach((key) => {
        result[key] = recomputeKpiFromRawData(kpis[key]);
    });
    return result;
};

export const getOverallKpis = (dailyReports, overallSummary, includeNoData) => {
    if (includeNoData || !dailyReports?.length) return overallSummary?.kpis || null;
    const kpiKeys = Object.keys(dailyReports[0]?.kpis || {});
    if (!kpiKeys.length) return null;
    const kpis = {};
    kpiKeys.forEach((key) => {
        let totalPass = 0;
        let total = 0;
        let title = '';
        let minutes = 0;
        let target = '';
        dailyReports.forEach((report) => {
            const recalc = recomputeKpiFromRawData(report.kpis?.[key]);
            totalPass += recalc.totalPass || 0;
            total += recalc.total || 0;
            if (!title && recalc.title) {
                title = recalc.title;
                minutes = recalc.minutes;
                target = recalc.target;
            }
        });
        // const score = total > 0 ? Math.ceil((totalPass / total) * 100) : 0;
        const score = total > 0 ? Math.round((totalPass / total) * 100) : 0;
        kpis[key] = { title, minutes, target, totalPass, total, score };
    });
    return kpis;
};
//#endregion

//#region components
const KpiRow = ({ kpi }) => {
    const targetNum = parseTarget(kpi.target);
    const meetsTarget = kpi.score >= targetNum;
    const passCount = kpi.totalPass ?? kpi.pass ?? kpi.passCount;
    const total = kpi.total ?? kpi.totalAssisted;

    return (
        <tr>
            <td style={labelCellStyle}>{kpi.title}</td>
            <td style={cellStyle}>{kpi.minutes} min</td>
            <td style={cellStyle}>{kpi.target}</td>
            <td style={cellStyle}>{passCount}</td>
            <td style={cellStyle}>{total}</td>
            <td style={{
                ...cellStyle,
                fontWeight: 600,
                backgroundColor: total === 0 ? '#f9fafb' : meetsTarget ? '#dcfce7' : '#fee2e2',
                color: total === 0 ? '#6b7280' : meetsTarget ? '#166534' : '#991b1b',
            }}>
                {total === 0 ? '-' : `${kpi.score}%`}
            </td>
        </tr>
    );
};

const KpiTable = ({ title, kpis }) => {
    const kpiKeys = kpis ? Object.keys(kpis) : [];
    if (!kpiKeys.length) return null;

    return (
        <div style={{ marginBottom: '24px' }}>
            <table style={tableStyle}>
                {SUMMARY_COLGROUP}
                <thead>
                    <tr>
                        <th style={headerStyle}>{title}</th>
                        <th style={headerStyle}>Minutes</th>
                        <th style={headerStyle}>Target</th>
                        <th style={headerStyle}>Total Pass</th>
                        <th style={headerStyle}>Total</th>
                        <th style={headerStyle}>Score</th>
                    </tr>
                </thead>
                <tbody>
                    {kpiKeys.map((key) => (
                        <KpiRow key={key} kpi={kpis[key]} />
                    ))}
                </tbody>
            </table>
        </div>
    );
};
//#endregion

const TerminalReceptionSummary = ({ dailyReports, overallSummary, includeNoData = false, isMultiDay = false }) => {
    if (!dailyReports?.length) return <p>No terminal reception data available.</p>;

    const overallKpis = getOverallKpis(dailyReports, overallSummary, includeNoData);

    return (
        <div>
            {overallKpis && (
                <KpiTable title="Overall Summary" kpis={overallKpis} />
            )}
            {isMultiDay && dailyReports.map((report) => (
                <KpiTable key={report.date} title={report.date} kpis={getKpisForReport(report, includeNoData)} />
            ))}
        </div>
    );
};

export default TerminalReceptionSummary;
